import styled, { css } from 'styled-components';

import { BaseButton } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { H2, H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

import { breakPoints } from '~/src/constants/breakpoints';
import { BreakPointsMFirst } from '~/src/constants/breakpoints';
import { GRID_ORDER_CATEGORY } from '~/src/features/yandex-landing/constants';
import type { TGraphicItem, TImgBlock, TLabel } from '~/src/features/yandex-landing/PlusesBlock/types';

import { Container } from '~/src/features/yandex-landing/styled';

export const PlusesContainer = styled(Container)`
    width: 100%;
    box-sizing: content-box;
    padding: 0 90px;
    justify-content: center;

    @media (max-width: ${breakPoints.sDesktop}) {
        padding: 0 34px;
    }

    @media (max-width: ${breakPoints.tablet}) {
        margin-top: 27px;
    }

    @media (max-width: ${breakPoints.mobile}) {
        max-width: 288px;
        margin-top: 46px;
        padding: 0 16px;
    }
`;

export const PlusesTitle = styled(H2)`
    ${FONTS.H2};
    text-align: center;

    @media (max-width: ${breakPoints.tablet}) {
        ${FONTS.H3};
        padding: 0;
    }
`;

export const GraphicBlock = styled.div`
    display: grid;
    width: 100%;
    height: 680px;
    margin-top: 32px;
    grid-template-rows: 1fr 1.85fr;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 32px;
    grid-template-areas:
        '${GRID_ORDER_CATEGORY.desktop.firstRow}'
        '${GRID_ORDER_CATEGORY.desktop.secondRow}';

    @media (max-width: ${breakPoints.tablet}) {
        height: 878px;
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr;
        grid-template-areas:
            '${GRID_ORDER_CATEGORY.tablet.firstRow}'
            '${GRID_ORDER_CATEGORY.tablet.secondRow}';
    }

    @media (max-width: ${breakPoints.mobile}) {
        height: 1780px;
        grid-template-rows: auto;
        grid-template-columns: 1fr;
        grid-template-areas:
            '${GRID_ORDER_CATEGORY.mobile.firstRow}'
            '${GRID_ORDER_CATEGORY.mobile.secondRow}'
            '${GRID_ORDER_CATEGORY.mobile.thirdRow}'
            '${GRID_ORDER_CATEGORY.mobile.fourthRow}';
    }
`;

export const WantButton = styled(BaseButton)`
    width: 327px;
    margin: 48px auto 0 auto;
    ${FONTS.S};

    @media (max-width: ${breakPoints.mobile}) {
        width: 100%;
    }
`;

export const GraphicItem = styled.div<TGraphicItem>`
    grid-area: ${({ area }) => area};
    border-radius: ${BorderRadius.OuterBlockRadius};
    padding: 22px 26px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    box-shadow: 0 4px 24px rgba(0, 0, 0, .08);

    ${(props) => props.gradient && css`
        background-image: linear-gradient(${props.gradient});
    `};

    ${(props) => props.layoutImg && css`
        background-image: url('${props.layoutImg}');
        background-size: 155%;

        @media (min-width: ${BreakPointsMFirst.sDesktop}) {
            background-size: 121%;
        }
    `};

    @media (max-width: ${breakPoints.tablet}) {
        height: 420px;
        padding: 22px 20px 30px 20px;
    }

    @media (max-width: ${breakPoints.mobile}) {
        padding-right: 14px;
        padding-left: 14px;
    }

    ${(props) => props.theme.initialTypeIsRow && css`
        gap: 15px;

        @media (min-width: ${BreakPointsMFirst.mobile}) {
            gap: 18px;
            margin-top: 5px;
        }

        @media (min-width: ${BreakPointsMFirst.tablet}) {
            padding-right: 56px;
            gap: 20px;
        }

        @media (min-width: ${BreakPointsMFirst.sDesktop}) {
            padding-right: 46px;
        }

        @media (min-width: ${BreakPointsMFirst.desktop}) {
            padding: 26px 57px 31px 26px;
        }
    `};
`;

export const StepAround = styled.div`
    position: absolute;
    width: 56px;
    height: 56px;
    justify-content: center;
    background-color: ${COLORS.BgMain};
    border-radius: 30px;
    ${FONTS.H1};
`;

export const WrapperTop = styled.div`
    width: 100%;

    @media (max-width: ${breakPoints.tablet}) {
        justify-content: end;
        height: 56px;
    }
`;

export const Label = styled.div<TLabel>`
    width: 135px;
    height: 32px;
    margin-left: 80px;
    margin-top: 12px;
    ${(props) => props.imgUrl && css`
        background-image: url('${props.imgUrl.desktop}');
        background-repeat: no-repeat;
        background-size: 100% 100%;

        @media (max-width: ${breakPoints.tablet}) {
            width: 164px;
            height: 39px;
            margin-top: 8px;
            background-image: url('${props.imgUrl.mobile}');
        }
    `};
`;

export const PriceMaxiBlock = styled.div<TImgBlock>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    width: 93.82px;
    height: 24.55px;
    top: 30%;
    left: 48%;
    font-size: 14.03px;

    @media (max-width: ${breakPoints.sDesktop}) {
        width: 93.82px;
        height: 24.55px;
        top: 32%;
        left: 48%;
        font-size: 14.03px;
    }

    @media (max-width: ${breakPoints.tablet}) {
        width: 105.53px;
        height: 26.86px;
        top: 30%;
        left: 48%;
        font-size: 15.35px;
    }

    @media (max-width: ${breakPoints.mobile}) {
        width: 105.53px;
        height: 26.86px;
        top: 25%;
        left: 43%;
        font-size: 15.35px;
    }

    ${(props) => props.imgUrl && css`
        background-image: url('${props.imgUrl}');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-size: 100%;
    `};
`;

export const PriceLightBlock = styled.div<TImgBlock>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    text-align: center;

    width: 87.68px;
    height: 25.43px;
    top: 45%;
    left: 40%;
    font-size: 14.03px;

    @media (max-width: ${breakPoints.sDesktop}) {
        width: 87.68px;
        height: 25.43px;
        top: 46%;
        left: calc(32vw - 235px);
        font-size: 14.03px;
    }

    @media (max-width: ${breakPoints.tablet}) {
        width: 95.23px;
        height: 27.82px;
        top: 45%;
        left: 38%;
        font-size: 15.35px;
    }

    @media (max-width: ${breakPoints.mobile}) {
        width: 95.23px;
        height: 27.82px;
        top: 38%;
        left: 28%;
        font-size: 15.35px;
    }

    ${(props) => props.imgUrl && css`
        background-image: url('${props.imgUrl}');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-size: 100%;
    `};
`;

export const ImgBlock = styled.div<TImgBlock>`
    width: calc(100% + 52px);
    height: 297px;
    border-radius: 16px;
    margin: -22px -26px 0 -26px;

    @media (max-width: ${breakPoints.sDesktop}) {
        ${(props) => props.hasImgStyle?.tablet && css`
            background-size: 120%;
        `};
        background-position: center;
        height: 350px;
    }

    @media (max-width: ${breakPoints.tablet}) {
        width: calc(100% + 40px);
        height: 317px;
        background-position: center;
        margin: -22px -20px 0 -20px;
    }

    @media (max-width: ${breakPoints.mobile}) {
        width: calc(100% + 28px);
        margin-right: -14px;
        margin-left: -14px;
    }

    ${(props) => props.imgUrl && css`
        background-image: url('${props.imgUrl}');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
    `};

    ${(props) => props.hasImgStyle?.tablet && css`
        @media (max-width: ${breakPoints.tablet}) {
            background-size: auto;
        }
    `};

    ${(props) => props.hasImgStyle?.mobile && css`
        @media (max-width: ${breakPoints.mobile}) {
            background-size: 130%;
            background-position: 65% 15%;
        }
    `};

    ${(props) => props.theme.initialTypeIsRow && css`
        @media (max-width: ${breakPoints.tablet}) {
            width: 213px;
            height: 105px;
            background-size: contain;
            background-position: left;
            margin: 0;
        }

        @media (min-width: ${BreakPointsMFirst.tablet}) {
            width: 341px;
            height: 170px;
            margin-top: -67px;
        }
  `};
`;

export const TextBlock = styled.div`
    height: fit-content;
    gap: 8px;

    ${(props) => !props.theme.initialTypeIsRow && css`
        @media (max-width: ${breakPoints.sDesktop}) {
            margin-top: -45px;
        }
    `};

    ${(props) => props.theme.initialTypeIsRow && css`
        color: ${COLORS.TextWhite};
        flex-direction: column;
    `};
`;

export const ContentWrap = styled.div`
    justify-content: flex-start;
    flex-direction: column;
    gap: 0;

    @media (max-width: ${breakPoints.tablet}) {
        justify-content: space-between;
    }

    ${(props) => !props.theme.initialTypeIsRow && css`
        @media (min-width: ${BreakPointsMFirst.mobile}) and (max-width: ${breakPoints.tablet}) {
            gap: 40px;
            justify-content: start;
        }
    `};

    ${(props) => props.theme.initialTypeIsRow && css`
        @media (min-width: ${BreakPointsMFirst.mobile}) {
            gap: 14px;
        }
    `};

    ${(props) => props.theme.initialTypeIsRow && css`
        @media (min-width: ${BreakPointsMFirst.tablet}) {
            flex-direction: row-reverse;
            flex-basis: 100%;
            justify-content: space-between;
        }
    `};
`;

export const Title = styled(H3)`
    ${FONTS.H3};
    white-space: pre;

    ${(props) => props.theme.initialTypeIsRow && css`
        white-space: initial;
        color: ${COLORS.TextWhite};
        @media (max-width: ${breakPoints.tablet}) {
            white-space: pre;
        }
    `};
`;

export const SubText = styled.div`
    ${FONTS.S};
    white-space: pre-wrap;

    @media (max-width: ${breakPoints.tablet}) {
        ${FONTS.XS};
    }

    ${(props) => props.theme.initialTypeIsRow && css`
        @media (min-width: ${BreakPointsMFirst.tablet}) and (max-width: ${breakPoints.sDesktop}) {
            width: 370px;
        }
    `};
`;
